import React from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import palette from 'core-system/Themes/palette'
import WalletCard from './WalletCard'
import BudgetProgressBar from './BudgetProgressBar'

const SectionContainer = styled.div`
  ${(props) => props.theme.baseCard};
  display: flex;
  flex-direction: column;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`

const AddCardLink = styled.span`
  color: ${(props) => props.theme.palette.primary.pink500};
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`

const CardsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    flex-direction: column;
  }
`

const CardWrapper = styled.div`
  flex: 1;
`

const BudgetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const BudgetsHeader = styled.div`
  margin-bottom: 0.375rem;
`

// Calculate days until reset based on end of month (default cycle)
const calculateDaysUntilReset = (): number => {
  const today = new Date()
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  return lastDay.getDate() - today.getDate()
}

const MyWalletSection: React.FC = () => {
  const { wallet } = useSelector((state: AppState) => state.employeePlatform)

  // Mock Clipper card data (as this may not be in Redux)
  const clipperCard = {
    name: 'Clipper Card',
    lastFour: '4821',
    balance: 4550, // in cents
  }

  // Fleet card from Redux wallet
  const fleetCard = wallet?.cards?.[0]
  const fleetCardData = {
    name: 'Fleet Card',
    lastFour: fleetCard?.last4 || '9012',
    balance: wallet?.currentBalance || 15000, // in cents
  }

  // Pre-Tax Transit budget (hardcoded values in cents)
  const transitBudget = {
    current: 12700, // $127
    total: 34000, // $340
    daysUntilReset: calculateDaysUntilReset(),
  }

  // Micromobility budget (hardcoded values in cents)
  const micromobilityBudget = {
    current: 2500, // $25
    total: 7000, // $70
    daysUntilReset: calculateDaysUntilReset(),
  }

  // Flex budget (hardcoded for demo, values in cents)
  const flexBudget = {
    current: 4200,
    total: 6000,
    daysUntilReset: calculateDaysUntilReset(),
  }

  const handleReload = () => {
    // TODO: Navigate to reload page or open modal
    console.log('Reload card')
  }

  const handleAddCard = () => {
    // TODO: Navigate to add card flow
    console.log('Add card')
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <Text variant='h4'>My Wallet</Text>
        <AddCardLink onClick={handleAddCard}>+ Add Card</AddCardLink>
      </SectionHeader>

      <CardsContainer>
        <CardWrapper>
          <WalletCard
            name={fleetCardData.name}
            lastFour={fleetCardData.lastFour}
            balance={fleetCardData.balance}
            cardType='fleet'
            onReload={handleReload}
          />
        </CardWrapper>
        <CardWrapper>
          <WalletCard
            name={clipperCard.name}
            lastFour={clipperCard.lastFour}
            balance={clipperCard.balance}
            cardType='clipper'
            onReload={handleReload}
          />
        </CardWrapper>
      </CardsContainer>

      <BudgetsHeader>
        <Text variant='body2' textColor='secondary'>
          MONTHLY BUDGETS
        </Text>
      </BudgetsHeader>

      <BudgetsContainer>
        <BudgetProgressBar
          label='Pre-Tax Transit'
          current={transitBudget.current}
          total={transitBudget.total}
          daysUntilReset={transitBudget.daysUntilReset}
          color={palette.success.green500}
        />
        <BudgetProgressBar
          label='Flex'
          current={flexBudget.current}
          total={flexBudget.total}
          daysUntilReset={flexBudget.daysUntilReset}
          color={palette.secondary.purple500}
        />
        <BudgetProgressBar
          label='Micromobility'
          current={micromobilityBudget.current}
          total={micromobilityBudget.total}
          daysUntilReset={micromobilityBudget.daysUntilReset}
          color={palette.accent.blue500}
        />
      </BudgetsContainer>
    </SectionContainer>
  )
}

export default React.memo(MyWalletSection)
